import React, { ReactNode, RefObject, useRef } from "react";

import {
  AiChatState,
  AiFeature,
} from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import { UiMessage } from "@intelligems/ig-types/schemas/ai/ui-message";
import {
  ChatSource,
  DropZoneRef,
  ExecutionPanelSlotProps,
  ExecutionPanelSlots,
  MessageFlags,
  OnAiChatSend,
} from "@src/features/ai/components/WorkflowEditor/execution/types";
import { ExecutionPanelProvider } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelProvider";
import { ExecutionPanelBody } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelBody";
import {
  useFileUpload,
  UseFileUploadOptions,
} from "@src/api/ai/chat-attachments/use-file-upload";
import { PromptInputProvider } from "@src/components/ai-elements/prompt-input/PromptInputProvider";
import { DropZoneIndicator } from "@src/features/ai/components/WorkflowEditor/execution/upload/DropZoneIndicator";
import { SxProps } from "@mui/material/styles";
// Type-only: the turn subtree reaches back here for `ExecutionPanelProps`
// through the panel context, so a value import would close that loop at
// runtime. `import type` is erased, and the cycle never exists.
import type { RenderPart } from "@src/features/ai/components/WorkflowEditor/execution/turn/PartBuilder";
import type { RenderToolContent } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";

export type CopyIdOverride = {
  id: string;
  label: string;
};

export type ExecutionPanelProps = {
  feature: AiFeature;
  /**
   * Which workflow this panel runs, for a caller that runs one.
   *
   * **Optional, because not every surface using this panel is a workflow.** A
   * caller driving an agent has no workflow to name, and naming one anyway
   * means inventing an id that is passed along and dropped. It reaches
   * `onSend.chat` on the first message, so a handler that runs a workflow reads
   * it and one that does not ignores it.
   */
  workflowId?: string;
  onSend: OnAiChatSend;
  /** The conversation this panel shows and writes to. The shared chat unless a surface keeps its own. */
  chat?: ChatSource;
  version?: string;
  clearReasoningOnMessage?: boolean;
  /** Uploads are enabled by default with the chat-attachment scenario and five files per turn. */
  fileUpload?: UseFileUploadOptions;
  slots?: ExecutionPanelSlots;
  slotProps?: ExecutionPanelSlotProps;
  title?: ReactNode;
  ref?: RefObject<HTMLDivElement | null>;
  injectedChatState?: AiChatState | null;
  closeIcon?: ReactNode;
  /** Accessible name for the close control. Defaults to the shared AI-chat label. */
  closeLabel?: string;
  onClose?: () => void;
  onNewChat?: () => void;
  copyIdOverride?: CopyIdOverride;
  placeholder?: string;
  /**
   * View transform applied to each message before render. Receives the whole
   * message and returns a converted copy (raw chat state is never mutated) —
   * letting a consumer rewrite text parts (e.g. strip thinking) and add/drop
   * parts (e.g. suppress a redundant tool prompt) with full cross-part
   * context. Called for every role; branch on `message.role` to scope it.
   * Omit for no transform.
   */
  transformMessage?: (message: UiMessage) => UiMessage;
  /**
   * Render one item of the conversation yourself, ahead of the built-in
   * branches. Called for every item the panel is about to draw; return
   * `undefined` to decline and let the default rendering happen, or a node to
   * own that item — `null` included, which renders it as nothing.
   *
   * This is the escape hatch for a surface that needs its own component for
   * one kind of part (the audit's recommendation card is the case it was added
   * for) without forking the renderer. Prefer `transformMessage` when the
   * change is to the *content* of a part; use this when the change is to what
   * the part is *drawn as*. Omit for the default rendering throughout.
   *
   * **Why this is a function and `slots` are nodes, which is not an
   * inconsistency.** A slot names one place in the hierarchy, and each slot's
   * renderer sits in a different place with a different context around it — so
   * an already-built node per name is the whole answer, and the caller closes
   * over whatever that place needs. Every part rendered here is the opposite:
   * one place, one context, many items passing through it. What varies is the
   * item, so the customisation has to receive the item. That is why the
   * arguments are flat and complete rather than grouped — branching on them is
   * the feature, and a caller typically switches on `item.part.type` or
   * `item.messageRole` and defers on everything else.
   */
  renderPart?: RenderPart;
  /**
   * Draws the sections inside an open tool card: the steps a tool reported, its
   * input, its result. Handed every section the panel decided applies, so a
   * surface reorders or replaces what it cares about and hands the rest back to
   * `renderToolBlock`.
   *
   * Distinct from `renderPart`, which decides whether the part is a tool card
   * at all. This decides what goes inside one.
   */
  renderToolContent?: RenderToolContent;
  sx?: SxProps;
} & MessageFlags;

export const ExecutionPanel = (props: ExecutionPanelProps) => (
  <PromptInputProviderExecutionPanel {...props} />
);

// stage 1: wrap with PromptInputProvider
const PromptInputProviderExecutionPanel = (props: ExecutionPanelProps) => {
  const dropZoneRef = useRef<HTMLElement>(null);
  return (
    <PromptInputProvider dropZoneRef={dropZoneRef}>
      <FileUploadExecutionPanel dropZoneRef={dropZoneRef} {...props} />
    </PromptInputProvider>
  );
};

type FileUploadCreatorProps = {
  dropZoneRef: DropZoneRef;
} & ExecutionPanelProps;

// stage 2: use PromptInputProvider context to create fileUpload
const FileUploadExecutionPanel = (props: FileUploadCreatorProps) => {
  const fileUpload = useFileUpload(
    props.fileUpload ?? { scenario: "chat-attachment", maxPerTurn: 5 }
  );
  return (
    <ExecutionPanelProvider
      {...props}
      dropZoneRef={props.dropZoneRef}
      fileUpload={fileUpload}
    >
      <ExecutionPanelBody sx={props.sx} />
      <DropZoneIndicator />
    </ExecutionPanelProvider>
  );
};
